// Setup (#/setup/...): admins edit, supervisors read. Layout follows the prototype's renderSetup:
// section nav | companies & sites tree | detail. Sections: Sites, Mission templates, Dispositions, Authorities,
// Users (admin only: roles, skills and RingCentral extension IDs), Groups and routing (Phase 2), AI policies (Phase 3),
// Talk-down library (Phase 5, features.deterrence).

import { useState } from 'react';
import { api } from '../../api.ts';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { ErrorBox, Loading } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';
import { href, type SetupSection } from '../../router.ts';
import { useSession } from '../../session.tsx';
import { useRefreshOn } from '../../stream.tsx';
import { AiEditor } from './AiEditor.tsx';
import { AuthoritiesEditor } from './AuthoritiesEditor.tsx';
import { DispositionsEditor } from './DispositionsEditor.tsx';
import { GroupsEditor } from './GroupsEditor.tsx';
import { MissionTemplatesEditor } from './MissionTemplatesEditor.tsx';
import { SiteSetupView } from './SiteSetupView.tsx';
import { TalkdownEditor } from './TalkdownEditor.tsx';
import { UsersEditor } from './UsersEditor.tsx';
import { WorkstationsEditor } from './WorkstationsEditor.tsx';

const NAV: { key: SetupSection; label: string; icon: IconName; adminOnly?: boolean; agent?: boolean; deter?: boolean; phone?: boolean }[] = [
  { key: 'sites', label: 'Sites', icon: 'map' },
  { key: 'missions', label: 'Mission templates', icon: 'list' },
  { key: 'ai', label: 'AI policies', icon: 'spark', agent: true },
  { key: 'talkdown', label: 'Talk-down library', icon: 'speaker', deter: true },
  { key: 'dispositions', label: 'Dispositions', icon: 'tag' },
  { key: 'authorities', label: 'Authorities', icon: 'shield' },
  { key: 'groups', label: 'Groups and routing', icon: 'route' },
  { key: 'users', label: 'Users', icon: 'users', adminOnly: true },
  { key: 'workstations', label: 'Workstations', icon: 'phone', phone: true },
];

function SitesTree({ selected }: { selected: string | null }) {
  const load = useLoad((signal) => api.sites(undefined, signal), []);
  useRefreshOn(['replica'], load.reload, 1000);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const byCo = new Map<string, { name: string; id: string; live: boolean | null }[]>();
  for (const s of (load.data ?? []).filter((x) => x.active)) {
    if (needle && !`${s.name} ${s.companyName} ${s.siteCode ?? ''}`.toLowerCase().includes(needle)) continue;
    const l = byCo.get(s.companyName) ?? [];
    l.push({ name: s.name, id: s.id, live: null });
    byCo.set(s.companyName, l);
  }
  const cos = [...byCo.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <aside className="pane">
      <div className="treehead">
        <div className="lbl">Companies &amp; sites</div>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sites" aria-label="Search sites" />
      </div>
      <div className="tree">
        {load.error && !load.data ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load sites" />
        ) : !load.data ? (
          <Loading label="Loading sites…" />
        ) : cos.length === 0 ? (
          <div className="note">{needle ? 'No sites match.' : 'No sites yet.'}</div>
        ) : (
          cos.map(([co, list]) => (
            <div key={co}>
              <div className="co">
                <Icon name="lock" className="dim" />
                {co}
              </div>
              {list
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((s) => (
                  <a key={s.id} className={`si${selected === s.id ? ' on' : ''}`} href={href.setup('sites', s.id)} aria-current={selected === s.id ? 'page' : undefined}>
                    <span>{s.name}</span>
                  </a>
                ))}
            </div>
          ))
        )}
      </div>
      <div className="note treefoot">
        <Icon name="lock" /> Sites come from the ERP (imports until Phase 6). The SOC settings are edited here.
      </div>
    </aside>
  );
}

export function SetupScreen({ section, id, tab }: { section: SetupSection; id: string | null; tab: string | null }) {
  const s = useSession();
  const canEdit = s.isAdmin;
  let main;
  if (section === 'sites') {
    main = id ? (
      <section className="pane sdetail">
        <SiteSetupView key={id} id={id} tab={tab} canEdit={canEdit} />
      </section>
    ) : (
      <section className="pane sdetail">
        <div className="state empty">Pick a site to set up its missions, call procedure, instructions and go-live.</div>
      </section>
    );
  } else if (section === 'dispositions') main = <DispositionsEditor canEdit={canEdit} />;
  else if (section === 'groups') main = <GroupsEditor canEdit={canEdit} />;
  else if (section === 'ai') main = <AiEditor key={id ?? ''} siteId={id} />;
  else if (section === 'talkdown') main = <TalkdownEditor />;
  else if (section === 'missions') main = <MissionTemplatesEditor canEdit={canEdit} selectedId={id} />;
  else if (section === 'users')
    main = canEdit ? (
      <UsersEditor />
    ) : (
      <div className="state empty">
        <Icon name="lock" /> Users and roles are managed by admins.
      </div>
    );
  else if (section === 'workstations') main = <WorkstationsEditor />;
  else main = <AuthoritiesEditor canEdit={canEdit} selectedId={id} />;

  return (
    <div className={`setup${section === 'sites' ? '' : ' wide'}`}>
      <aside className="pane">
        <nav className="snav" aria-label="Setup sections">
          {NAV.filter((n) => (!n.adminOnly || canEdit) && (!n.agent || s.config.features.agent) && (!n.deter || s.config.features.deterrence) && (!n.phone || s.config.features.softphone)).map((n) => (
            <a key={n.key} href={href.setup(n.key)} className={section === n.key ? 'on' : ''} aria-current={section === n.key ? 'page' : undefined}>
              <Icon name={n.icon} />
              {n.label}
            </a>
          ))}
        </nav>
        {!canEdit && (
          <div className="note treefoot">
            <Icon name="lock" /> Supervisors can read setup. Admins edit it.
          </div>
        )}
      </aside>
      {section === 'sites' && <SitesTree selected={id} />}
      {section === 'sites' ? main : <section className="setup-main">{main}</section>}
    </div>
  );
}
